import React, { useEffect, useState } from 'react';
import { MapPin, Search } from 'lucide-react';

const formatCep = (value = '') => {
  const digits = String(value).replace(/\D/g, '').slice(0, 8);

  if (digits.length <= 5) {
    return digits;
  }

  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
};

const buildAddress = ({
  street = '',
  number = '',
  complement = '',
  neighborhood = '',
  city = '',
  state = '',
  cep = ''
}) => {
  const parts = [];

  const streetNumber = [street, number].filter(Boolean).join(', ');
  if (streetNumber) parts.push(streetNumber);

  if (complement) parts.push(complement);
  if (neighborhood) parts.push(neighborhood);

  const cityState = [city, state].filter(Boolean).join(' - ');
  if (cityState) parts.push(cityState);

  if (cep) parts.push(`CEP ${cep}`);

  return parts.join(', ');
};

const AddressCepField = ({
  value = '',
  onChange,
  required = false,
  inputClassName = ''
}) => {
  const [cep, setCep] = useState('');
  const [street, setStreet] = useState('');
  const [number, setNumber] = useState('');
  const [complement, setComplement] = useState('');
  const [neighborhood, setNeighborhood] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepError, setCepError] = useState('');

  const updateFinalAddress = (patch = {}) => {
    const next = {
      cep,
      street,
      number,
      complement,
      neighborhood,
      city,
      state,
      ...patch
    };

    const finalAddress = buildAddress(next);

    if (typeof onChange === 'function') {
      onChange(finalAddress);
    }
  };

  const handleCepChange = (event) => {
    const formatted = formatCep(event.target.value);
    setCep(formatted);
    setCepError('');
  };

  const handleSearchCep = async () => {
    const cleanCep = cep.replace(/\D/g, '');

    if (cleanCep.length !== 8) {
      setCepError('Informe um CEP com 8 dígitos.');
      return;
    }

    try {
      setLoadingCep(true);
      setCepError('');

      const response = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json/`
      );

      if (!response.ok) {
        throw new Error('Não foi possível consultar o CEP.');
      }

      const data = await response.json();

      if (data?.erro) {
        setCepError('CEP não encontrado.');
        return;
      }

      const nextAddress = {
        cep: formatCep(cleanCep),
        street: data.logradouro || '',
        neighborhood: data.bairro || '',
        city: data.localidade || '',
        state: data.uf || '',
        number,
        complement
      };

      setCep(nextAddress.cep);
      setStreet(nextAddress.street);
      setNeighborhood(nextAddress.neighborhood);
      setCity(nextAddress.city);
      setState(nextAddress.state);

      if (typeof onChange === 'function') {
        onChange(buildAddress(nextAddress));
      }
    } catch (error) {
      console.error('Erro ao consultar CEP:', error);
      setCepError('Não foi possível consultar o CEP. Digite o endereço manualmente.');
    } finally {
      setLoadingCep(false);
    }
  };

  useEffect(() => {
    /*
     * Respostas antigas ou endereços já preenchidos continuam aparecendo
     * no campo manual. Não tentamos decompor automaticamente a string,
     * evitando alterar ou interpretar incorretamente dados históricos.
     */
  }, [value]);

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-center gap-2 mb-3 text-blue-800 font-medium">
          <MapPin className="w-4 h-4" />
          Preenchimento do endereço pelo CEP
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={cep}
            onChange={handleCepChange}
            onBlur={() => {
              if (cep.replace(/\D/g, '').length === 8) {
                handleSearchCep();
              }
            }}
            maxLength={9}
            inputMode="numeric"
            placeholder="00000-000"
            className="flex-1 px-3 py-2 border border-blue-200 rounded-lg bg-white"
          />

          <button
            type="button"
            onClick={handleSearchCep}
            disabled={loadingCep}
            className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
          >
            <Search className="w-4 h-4 mr-2" />
            {loadingCep ? 'Buscando...' : 'Buscar CEP'}
          </button>
        </div>

        {cepError && (
          <p className="mt-2 text-sm text-red-600">
            {cepError}
          </p>
        )}

        {(street || neighborhood || city || state) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logradouro
              </label>

              <input
                type="text"
                value={street}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setStreet(nextValue);
                  updateFinalAddress({ street: nextValue });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número
              </label>

              <input
                type="text"
                value={number}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setNumber(nextValue);
                  updateFinalAddress({ number: nextValue });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                placeholder="Número"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Complemento
              </label>

              <input
                type="text"
                value={complement}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setComplement(nextValue);
                  updateFinalAddress({ complement: nextValue });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
                placeholder="Apartamento, sala, bloco..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bairro
              </label>

              <input
                type="text"
                value={neighborhood}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setNeighborhood(nextValue);
                  updateFinalAddress({ neighborhood: nextValue });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cidade
              </label>

              <input
                type="text"
                value={city}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setCity(nextValue);
                  updateFinalAddress({ city: nextValue });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estado
              </label>

              <input
                type="text"
                value={state}
                onChange={(event) => {
                  const nextValue = event.target.value
                    .toUpperCase()
                    .slice(0, 2);

                  setState(nextValue);
                  updateFinalAddress({ state: nextValue });
                }}
                maxLength={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white"
              />
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Endereço completo
        </label>

        <textarea
          value={value}
          onChange={(event) => {
            if (typeof onChange === 'function') {
              onChange(event.target.value);
            }
          }}
          required={required}
          rows={3}
          className={
            inputClassName ||
            'w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white'
          }
          placeholder="O endereço será preenchido pelo CEP, mas também pode ser digitado ou corrigido manualmente."
        />
      </div>
    </div>
  );
};

export default AddressCepField;